import React, {
  forwardRef,
  useRef,
  useState,
  type InputHTMLAttributes,
  type ReactNode,
} from "react";

import clsx from "clsx";
import { get } from "lodash";

interface TextInputProps extends InputHTMLAttributes<HTMLInputElement> {
  name?: string;
  label?: string | React.ReactElement;
  className?: string;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  width?: string;
  maxWidth?: string;
  errorMessage?: string;
  errors?: any;
  isDisabled?: boolean;
  isRequired?: boolean;
  inputClassName?: string;
}

export const FileInput = forwardRef<HTMLInputElement, TextInputProps>(
  (
    {
      name,
      label,
      className,
      inputClassName,
      leftIcon = null,
      rightIcon = null,
      width,
      maxWidth,
      errorMessage,
      errors,
      isDisabled = false,
      isRequired = false,
      onChange,
      ...props
    },
    ref,
  ) => {
    // Access the error message from react-hook-form
    const formErrorMessage = get(errors, `${name}.message`);
    const hasError = errorMessage || formErrorMessage;

    const instance = useRef<HTMLInputElement | null>(null);

    const [selectedFile, setSelectedFile] = useState("");

    return (
      <div
        className={clsx("nb-input-container group/input", className, {
          "pointer-events-none": isDisabled,
          "has-label": label,
        })}
        style={{ width, maxWidth }}
      >
        {label && (
          <label className={clsx("label", isDisabled && "text-disabled")}>
            {label}
            {isRequired && <span className="text-mars">*</span>}
          </label>
        )}
        <span
          className={clsx(
            leftIcon &&
              clsx(
                "absolute-vertical-center group-hover/input:text-cosmic",
                "group-focus-within/input:text-cosmic",
              ),
          )}
        >
          {leftIcon && <span className="pl-2.5">{leftIcon}</span>}
        </span>
        <div
          className={clsx(
            "nb-input cursor-text",
            rightIcon && "pr-10",
            leftIcon && "pl-10",
            inputClassName,
            {
              "opacity-60": isDisabled,
            },
          )}
          onClick={() => {
            instance.current?.click();
          }}
        >
          {selectedFile ? (
            <div className="text-sm">{selectedFile}</div>
          ) : (
            <div className="text-sm">Click here to upload</div>
          )}
        </div>
        <input
          ref={(ins) => {
            if (typeof ref === "function") {
              ref(ins);
            } else if (ref) {
              ref.current = ins;
            }
            instance.current = ins;
          }}
          name={name}
          className="hidden"
          type="file"
          disabled={isDisabled}
          required={isRequired}
          onChange={(event) => {
            if (event.target.files) {
              const file = event.target.files[0];
              setSelectedFile(file.name);
            }
            onChange?.(event);
          }}
          {...props}
        />
        <span className={clsx(rightIcon && "absolute-vertical-center right-0")}>
          {rightIcon && <span className="pr-2.5">{rightIcon}</span>}
        </span>
        {hasError && (
          <div className="error-message">
            {errorMessage ?? formErrorMessage}
          </div>
        )}
      </div>
    );
  },
);
